import classNames from "classnames";
import { AddToFavoritesButton } from "components/AddToFavoriteButton";
import { Checkbox } from "components/Checkbox/Checkbox";
import Clickable from "components/Clickable";
import { ButtonPrimary } from "components/Clickable/Buttons";
import { AccountOverviewList } from "components/Favorites/interfaces";
import { LoadingOverlay } from "components/Loading/LoadingOverlay";
import { RulesetProvider, UserStatus } from "components/productContext";
import { useRouterParams } from "components/useRouterParams";
import {
    fetchAccountInfo,
    fetchFavoritesLists,
} from "components/wagtail/ProductPage";
import urls from "helpers/urls";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";
import {
    Button,
    Disclosure,
    DisclosurePanel,
    Heading,
} from "react-aria-components";

import CaretIcon from "../../public/svg/caret.svg";
import Checkmark from "../../public/svg/checkmark.svg";
import XIcon from "../../public/svg/x.svg";
import VisualizerFeedbackForm from "./VisualizerFeedbackForm";
import { VisualizerPanel } from "./VisualizerPanel";

import styles from "./visualizer.module.scss";

export interface IVisualizerProps {
    steps: Array<IVisualizerStepProps>;
    referID: string | null;
}

export interface IVisualizerStepProps {
    title: string;
    order: number;
    rules: Array<IVisualizerPanelRulesProps>;
    code_strings: Array<string>;
    render_url?: null | string;
}

export interface IVisualizerPanelRulesProps {
    id: number;
    title: string;
    order: number;
    filter_rule: IVisualizerPanelRulesProps | null;
    options: Array<IVisualizerPanelOptionProps>;
    selected: number | null;
    linked_rule?: number;
}

export interface IVisualizerPanelOptionProps {
    id: number;
    option: string;
    primary_image: {
        title: string;
        image: string | null;
        asset_type: string;
    } | null;
    finish_images: { [key: string]: string } | null;
    obj_type: {
        id: number;
        name: string;
        display_name: string;
        order: number;
    };
    themes: Array<{
        id: Number;
        name: string;
    }>;
}

export const getSelectedChoices = (steps: IVisualizerStepProps[]) => {
    const choices: { rule: number; option: number }[] = [];

    steps.flatMap((step) => {
        return step.rules.map((rule) => {
            if (rule.selected) {
                choices.push({ rule: rule.id, option: rule.selected });
            }

            if (rule.filter_rule && rule.filter_rule.selected) {
                choices.push({
                    rule: rule.filter_rule.id,
                    option: rule.filter_rule.selected,
                });
            }
        });
    });

    return choices;
};

export const getSelectedOptions = (steps: IVisualizerStepProps[]) => {
    const options: IVisualizerPanelOptionProps[] = [];

    steps.flatMap((step) => {
        return step.rules.map((rule) => {
            if (rule.selected) {
                const selectedOption = rule.options.find((option) => {
                    return option.id.toString() === rule.selected?.toString();
                });
                if (selectedOption) {
                    options.push(selectedOption);
                }
            }

            if (rule.filter_rule && rule.filter_rule.selected) {
                const selectedFilterOption = rule.filter_rule.options.find(
                    (option) =>
                        option.id.toString() ===
                        rule.filter_rule?.selected?.toString(),
                );
                if (selectedFilterOption) {
                    options.push(selectedFilterOption);
                }
            }
        });
    });

    return options;
};

export const buildStepSummaryList = (step: IVisualizerStepProps) => {
    return step.rules.map((rule, j) => {
        let optionTitle;
        const ruleOption = rule.options.find(
            (o) => o.id.toString() === rule.selected?.toString(),
        );
        if (ruleOption) {
            let filterOption;
            if (rule.filter_rule) {
                const filterRuleOption = rule.filter_rule.options.find(
                    (o) =>
                        o.id.toString() ===
                        rule.filter_rule!.selected?.toString(),
                );

                if (filterRuleOption) {
                    filterOption = filterRuleOption;
                }
            }

            if (filterOption) {
                optionTitle = `${ruleOption.option} in ${filterOption.option}`;
            }
        } else {
            const filterRuleOption = rule.options.find(
                (o) => o.id.toString() === rule.selected?.toString(),
            );
            if (filterRuleOption) {
                optionTitle = filterRuleOption.option;
            }
        }

        if (!optionTitle) {
            return <span key={j}></span>;
        }

        return (
            <li key={j}>
                <div>{rule.title}</div>
                {optionTitle}
            </li>
        );
    });
};

export const Visualizer = ({ steps, referID }: IVisualizerProps) => {
    const router = useRouter();
    const [nextStepValid, setNextStepValid] = useState(false);
    const [showMobileRender, setShowMobileRender] = useState(true);
    const [favoritesLists, setFavoritesLists] = useState<AccountOverviewList[]>(
        [],
    );
    const [userStatus, setUserStatus] = useState<UserStatus>("unregistered");
    const [dataLoading, setDataLoading] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);

    const routerParams = useRouterParams({
        method: "push",
        shallow: false,
        scroll: false,
    });

    const checkStepCompleted = useCallback((step: IVisualizerStepProps) => {
        if (!step) {
            return false;
        }

        return step.rules.every((rule) => {
            return rule.selected && rule.filter_rule?.selected;
        });
    }, []);

    const allRulesSelected = () => {
        let allRulesSelected = true;

        allRulesSelected = stepArray.every((step) => {
            return checkStepCompleted(step);
        });

        return allRulesSelected;
    };

    const getCurrentStepIndex = (
        routerStep: string | string[] | undefined,
    ): number => {
        if (routerStep) {
            const stepNum = Number(routerStep);

            if (stepNum < 0) {
                return 0;
            } else if (
                stepNum < stepArray.length &&
                checkStepCompleted(stepArray[stepNum - 1])
            ) {
                return stepNum;
            } else if (stepNum >= stepArray.length && allRulesSelected()) {
                return stepArray.length;
            }
        }

        return 0;
    };

    const stepArray = steps ? Object.values(steps) : [];
    const currentStepIndex = getCurrentStepIndex(
        routerParams.getParamValue("currentStep"),
    );
    const currentStep = stepArray[currentStepIndex];
    const displaySummary = currentStepIndex == stepArray.length;
    const mirrorOptions =
        (routerParams.getParamValue("mirror") ?? "true") === "true";

    let brassGradientStop = "0";
    if (displaySummary) {
        brassGradientStop = "100";
    } else if (currentStepIndex === 1) {
        brassGradientStop = "50";
    }

    const firstRuleLinked = () => {
        let firstRuleLinked = true;

        firstRuleLinked = stepArray
            .sort((a, b) => a.order - b.order)[0]
            .rules.some((rule) => {
                return (
                    rule.linked_rule ||
                    (rule.filter_rule && rule.filter_rule.linked_rule)
                );
            });

        return firstRuleLinked;
    };

    const getFirstSelectedOption = () => {
        const firstRule = stepArray
            .sort((a, b) => a.order - b.order)[0]
            .rules.sort((a, b) => a.order - b.order)[0];
        return firstRule.options.find(
            (o) => o.id.toString() === firstRule.selected?.toString(),
        );
    };

    const getReturnURLObject = () => {
        const referURL = routerParams.getParamValue("referURL") as string;
        const referTitle = routerParams.getParamValue("referTitle") as string;

        if (!referURL || !referTitle) {
            return null;
        }

        return {
            url: decodeURIComponent(referURL),
            title: decodeURIComponent(referTitle),
        };
    };

    const getNextStep = () => {
        if (currentStepIndex < stepArray.length - 1) {
            return stepArray[currentStepIndex + 1];
        } else {
            return null;
        }
    };

    const returnURLObj = getReturnURLObject();

    if (router.events)
        router.events.on("routeChangeComplete", () =>
            setShowMobileRender(true),
        );

    const setRouterCurrentStep = (newIndex: number) => {
        routerParams.setParam("currentStep", newIndex.toString());
    };

    useEffect(() => {
        // TODO: Move this santization logic to the BE
        const stepNum = Number(routerParams.getParamValue("currentStep"));
        if (
            (stepNum < stepArray.length &&
                !checkStepCompleted(stepArray[stepNum - 1])) ||
            (stepNum >= stepArray.length && !allRulesSelected())
        ) {
            routerParams.clearParams("currentStep");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchFavoritesLists().then((response) =>
            setFavoritesLists(response.results),
        );
        fetchAccountInfo(setUserStatus);

        const startLoading = () => setDataLoading(true);
        const stopLoading = () => {
            setDataLoading(false);
        };

        if (router.events) {
            router.events.on("routeChangeStart", startLoading);
            router.events.on("routeChangeComplete", stopLoading);
        }

        return () => {
            if (router.events) {
                router.events.off("routeChangeStart", startLoading);
                router.events.off("routeChangeComplete", stopLoading);
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentStep) {
            setNextStepValid(checkStepCompleted(currentStep));
        }
    }, [checkStepCompleted, currentStep]);

    const buildSummary = () => {
        return (
            <>
                {stepArray.map((step, i) => {
                    return (
                        <section key={i} className={styles.summarySection}>
                            <header>
                                <p>{step.title}</p>
                                <Clickable
                                    className={styles.summaryEditButton}
                                    unstyled={true}
                                    onClick={() => {
                                        const stepIndex = stepArray.findIndex(
                                            (s) => s === step,
                                        );
                                        setRouterCurrentStep(stepIndex);
                                    }}
                                >
                                    Edit
                                    <CaretIcon />
                                </Clickable>
                            </header>
                            <div>
                                <ul className={styles.summaryList}>
                                    {buildStepSummaryList(step)}
                                </ul>
                                <div className={styles.summaryCodeTitle}>
                                    Product Code
                                </div>
                                {step.code_strings.length > 0 ? (
                                    <Disclosure className={styles.summaryCodes}>
                                        {({ isExpanded }) => (
                                            <>
                                                <DisclosurePanel
                                                    className={
                                                        styles.rulesetDetailsPanel
                                                    }
                                                >
                                                    <ul>
                                                        {step.code_strings.map(
                                                            (
                                                                code: string,
                                                                i,
                                                            ) => {
                                                                return (
                                                                    <li key={i}>
                                                                        {code}
                                                                    </li>
                                                                );
                                                            },
                                                        )}
                                                    </ul>
                                                </DisclosurePanel>
                                                <Heading
                                                    className={
                                                        styles.showSummaryCodesButton
                                                    }
                                                >
                                                    <Button slot="trigger">
                                                        {isExpanded
                                                            ? "Hide codes"
                                                            : "Show codes"}
                                                    </Button>
                                                </Heading>
                                            </>
                                        )}
                                    </Disclosure>
                                ) : (
                                    // TODO: Throw some error that we can log, as this should only happen if we've misconfigured things
                                    <p className={styles.summaryCodeError}>
                                        Contact a dealer to see if this product
                                        comes in the selected configuration
                                    </p>
                                )}
                            </div>
                        </section>
                    );
                })}
            </>
        );
    };

    const buildRender = (mobile: boolean) => {
        if (!displaySummary && !currentStep.render_url) {
            return null;
        }

        if (displaySummary) {
            return stepArray.map((step) => (
                // Todo: get flip info from the ruleset
                <div key={step.render_url + step.title}>
                    <PreviewImage
                        step={step}
                        flip={step.title === "Interior"}
                        mobile={mobile}
                    />
                </div>
            ));
        }

        // Todo: get flip info from the ruleset
        return (
            <PreviewImage
                step={currentStep}
                flip={currentStep.title === "Interior"}
                mobile={mobile}
            />
        );
    };

    const domain =
        typeof window !== "undefined"
            ? window.location.protocol +
              "//" +
              window.location.hostname +
              window.location.port +
              "/"
            : "";

    return (
        <div className={styles.root}>
            {returnURLObj && (
                <Clickable
                    unstyled={true}
                    href={returnURLObj.url}
                    className={styles.returnLink}
                >
                    <CaretIcon />
                    Back to <span>{returnURLObj.title}</span>
                </Clickable>
            )}
            <header>
                <h1 className="h3">Create Your Own Product</h1>
                <div className={styles.stepTracker}>
                    <div
                        className={styles.stepTrackerBar}
                        style={{
                            background: `linear-gradient(to right, #dbc598 ${brassGradientStop}%, #b9bdc1 ${brassGradientStop}%, #b9bdc1)`,
                        }}
                    ></div>
                    {stepArray.map((step: IVisualizerStepProps, i) => {
                        const isPreviousStep = i < currentStepIndex;

                        return (
                            <button
                                key={i}
                                disabled={
                                    isPreviousStep || displaySummary
                                        ? false
                                        : true
                                }
                                onClick={() => {
                                    const stepIndex = stepArray.findIndex(
                                        (s) => s === step,
                                    );
                                    setRouterCurrentStep(stepIndex);
                                }}
                            >
                                <div
                                    className={classNames({
                                        [styles.stepSVG]: true,
                                        [styles.currentStep]:
                                            currentStepIndex <
                                                stepArray.length &&
                                            currentStep.title === step.title,
                                        [styles.previousStep]: isPreviousStep,
                                    })}
                                >
                                    <svg width="28" height="28">
                                        <circle cx="14" cy="14" r="14" />
                                    </svg>
                                    {(isPreviousStep || displaySummary) && (
                                        <Checkmark
                                            width="16"
                                            height="16"
                                            className={styles.checkmark}
                                        />
                                    )}
                                </div>
                                {step.title}
                            </button>
                        );
                    })}
                    <button
                        disabled={
                            getNextStep() ||
                            currentStepIndex + 1 === stepArray.length
                                ? true
                                : false
                        }
                        onClick={() => setRouterCurrentStep(stepArray.length)}
                    >
                        <div
                            className={classNames({
                                [styles.stepSVG]: true,
                                [styles.currentStep]: displaySummary,
                            })}
                        >
                            <svg width="28" height="28">
                                <circle cx="14" cy="14" r="14" />
                            </svg>
                        </div>
                        Summary
                    </button>
                </div>
            </header>
            <section className={styles.content}>
                <figure
                    className={classNames({
                        [styles.display]: true,
                        [styles.displaySummary]: displaySummary,
                    })}
                >
                    {buildRender(false)}
                </figure>
                <div>
                    {dataLoading && !panelOpen && (
                        <LoadingOverlay visuallyHiddenText="Visualizer data is loading" />
                    )}
                    <h2 className="h5">
                        {!displaySummary
                            ? currentStepIndex + 1
                            : stepArray.length + 1}
                        .{" "}
                        {!displaySummary
                            ? `Customize ${currentStep.title}`
                            : "Summary"}
                    </h2>
                    {!getNextStep() && !displaySummary && firstRuleLinked() && (
                        <Checkbox
                            value={"match previous options"}
                            onChange={() =>
                                routerParams.setParam(
                                    "mirror",
                                    (!mirrorOptions).toString(),
                                )
                            }
                            isSelected={mirrorOptions}
                        >
                            {`Use same options as ${
                                stepArray[currentStepIndex - 1].title
                            }`}
                        </Checkbox>
                    )}
                    {!displaySummary ? (
                        currentStep.rules
                            .filter((r) => {
                                return r.options.length > 0;
                            })
                            .sort((a, b) => a.order - b.order)
                            .map((rule, i, allRules) => {
                                return (
                                    <VisualizerPanel
                                        key={`VisualizerPanel-${currentStep.title}-${rule.title}--${i}`}
                                        rule={rule}
                                        ruleArray={allRules}
                                        currentStep={currentStep}
                                        nextStep={getNextStep()}
                                        onNextStep={(
                                            step: IVisualizerStepProps | null,
                                        ) => {
                                            if (step) {
                                                const stepIndex =
                                                    stepArray.findIndex(
                                                        (s) => s === step,
                                                    );
                                                setRouterCurrentStep(stepIndex);
                                            } else {
                                                setRouterCurrentStep(
                                                    stepArray.length,
                                                );
                                            }
                                        }}
                                        mobileRender={
                                            <>
                                                {showMobileRender &&
                                                    currentStep.render_url && (
                                                        <figure
                                                            className={
                                                                styles.displayMobile
                                                            }
                                                        >
                                                            {buildRender(true)}
                                                            <Clickable
                                                                unstyled={true}
                                                                onClick={() =>
                                                                    setShowMobileRender(
                                                                        false,
                                                                    )
                                                                }
                                                            >
                                                                <XIcon />
                                                                <VisuallyHidden>
                                                                    Close Panel
                                                                </VisuallyHidden>
                                                            </Clickable>
                                                        </figure>
                                                    )}
                                            </>
                                        }
                                        setMobileRender={setShowMobileRender}
                                        onOpen={setPanelOpen}
                                        dataLoading={dataLoading}
                                    />
                                );
                            })
                    ) : (
                        <div>{buildSummary()}</div>
                    )}
                    <section className={styles.panelButtons}>
                        {!displaySummary ? (
                            <ButtonPrimary
                                className={styles.nextButton}
                                onClick={() => {
                                    const nextStep = getNextStep();
                                    if (nextStep) {
                                        const stepIndex = stepArray.findIndex(
                                            (s) => s === nextStep,
                                        );
                                        setRouterCurrentStep(stepIndex);
                                    } else {
                                        setRouterCurrentStep(stepArray.length);
                                    }
                                }}
                                disabled={!nextStepValid}
                            >
                                <div>
                                    {getNextStep()
                                        ? `Next: ${
                                              stepArray[currentStepIndex + 1]
                                                  .title
                                          }`
                                        : "Next: Summary"}
                                    <CaretIcon className={styles.buttonCaret} />
                                </div>
                            </ButtonPrimary>
                        ) : (
                            <RulesetProvider
                                favoriteItem={null}
                                favoriteItemPieces={{
                                    image: stepArray[0].render_url
                                        ? [
                                              String(
                                                  domain +
                                                      stepArray[0].render_url,
                                              ),
                                          ]
                                        : [],
                                    page: Number(referID),
                                    choices: getSelectedChoices(stepArray),
                                    title: getFirstSelectedOption()
                                        ? `Customized ${
                                              getFirstSelectedOption()!.option
                                          }`
                                        : "Customized Product",
                                }}
                                favoritesLists={favoritesLists}
                                setFavoritesList={setFavoritesLists}
                                userStatus={userStatus}
                                setUserStatus={setUserStatus}
                            >
                                <AddToFavoritesButton isRuleset={true} />
                            </RulesetProvider>
                        )}
                        {!displaySummary && (
                            <Clickable
                                unstyled={true}
                                className={styles.dealerButton}
                                href={urls.dealerLocator}
                            >
                                Have questions? Want it now?
                                <div>
                                    Find a Dealer{" "}
                                    <CaretIcon className={styles.buttonCaret} />
                                </div>
                            </Clickable>
                        )}
                    </section>
                </div>
            </section>
        </div>
    );
};

function PreviewImage({
    step,
    showDoor = true,
    flip = true,
    mobile,
}: {
    step: IVisualizerStepProps;
    showDoor?: boolean;
    flip?: boolean;
    mobile?: boolean;
}) {
    const [imageLoading, setImageLoading] = useState(false);

    if (!step.render_url) return null;
    const url = new URL(step.render_url, "https://example.com");
    url.searchParams.set("engine", `cycles`);
    url.searchParams.set("door", `${showDoor}`);
    url.searchParams.set("resolution", `${500}`); // Temporary - speed up rendering for some client demos
    return (
        <>
            {!mobile && <figcaption>{step.title}</figcaption>}
            <img
                key={url.toString()}
                className={flip ? styles.renderFlipped : undefined}
                ref={(img) =>
                    setImageLoading(!img?.complete || img?.naturalWidth === 0)
                }
                onLoad={() => setImageLoading(false)}
                src={`${url.pathname}${url.search}`}
                alt={`${step.title} render - ${step.render_url}`}
            />
            {imageLoading && <span className={styles.renderLoading}></span>}
            <VisualizerFeedbackForm />
        </>
    );
}
